import React, { useContext, useEffect } from "react";

import { DispatchContext } from "../../../context/domain/planning/DispatchContext";

import SummaryBar from "./dailySummary/SummaryBar";
import JobBox from "./JobBox";
import DatesBar from "./DatesBar";

/**
 * Render the Dispatch day grid: DatesBar, optional SummaryBar/DayDetails, and JobBox.
 *
 * @param {Object} props
 * @param {string} [props.className=""] Optional wrapper class names.
 * @param {boolean} [props.hideDatesBar=false] When true, DatesBar is rendered
 *   outside this tree (sticky stack with TopBar). Capture clones keep
 *   DatesBar (hideDatesBar stays false).
 * @returns {JSX.Element}
 */
const DispatchContent = ({ className = "", hideDatesBar = false }) => {
  const { isDispatchCapturing, isReadOnly, compact } =
    useContext(DispatchContext);
  const showSummaryBar = !isReadOnly && !compact;
  const showReadOnlyBanner = isReadOnly && !compact;
  const showInlineDatesBar = !hideDatesBar;

  // Prevents the default action of the click and keydown events when in readOnly mode
  useEffect(() => {
    if (isReadOnly) {
      /**
       * Capture-phase guard: stop clicks/keys inside `.readonly` unless marked `.dont-block`.
       *
       * @param {Event} e Document-level event.
       * @returns {void}
       */
      const blockEvent = (e) => {
        const target = e.target;
        if (target.closest(`.readonly`) && !target.closest(`.dont-block`)) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      document.addEventListener("click", blockEvent, true);
      document.addEventListener("keydown", blockEvent, true);

      return () => {
        document.removeEventListener("click", blockEvent, true);
        document.removeEventListener("keydown", blockEvent, true);
      };
    }
  }, [isReadOnly]);

  return (
    <div
      className={`${className} px-2 font-inter bg-gray-100 ${isReadOnly ? "readonly pointer-events-none" : ""}`}
    >
      {showReadOnlyBanner && (
        <div className="my-2 bg-green-50 rounded-md shadow-md shadow-gray-500 font-bold text-emerald-950 text-center">
          READ ONLY MODE{" "}
        </div>
      )}
      {showInlineDatesBar ? (
        <div
          className={`z-10 ${
            isDispatchCapturing
              ? // Capture clone: non-sticky offset only (html2canvas); keep class hooks intact.
                "top-12 border-t-2 border-gray-700"
              : "sticky top-14"
          }`}
        >
          <DatesBar />
        </div>
      ) : null}
      {showSummaryBar ? (
        // mt-12 when DatesBar is inline (capture clone) so SummaryBar clears the sticky/overlay bar.
        <div className={showInlineDatesBar ? "mt-12" : "mt-0"}>
          <SummaryBar />
        </div>
      ) : null}
      <div
        className={
          !showSummaryBar
            ? compact
              ? "mt-2" // tighter under sticky DatesBar in project embeds
              : "mt-4"
            : isReadOnly
              ? "mt-16"
              : ""
        }
      >
        <JobBox />
      </div>
    </div>
  );
};

export default DispatchContent;
